// Live partners: go-live review, pause and resume, remove a paused partner (CHANGE-037), launch budget (one staff member
// sets it), used and left, points waiting, and spend problems (CHANGE-007 #3: partners have no data health page).
// CHANGE-070: remove and budget go through a confirm that states the partner and the amount; budget sends an
// idempotency key; "no problems" says what was checked and when spend last came in. One table, Live and Draft tabs, and a
// row's checklist, spend and actions open on the row. A partner waiting for review opens by itself.
import { useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api, cents, points } from '../../lib'
import { Button, Busy, Err, Fresh, Page, Section, Sources, Strip, errorText, useCursor, useKit } from './kit'
import { Facts, parseDollars, useStaffAction, when } from './common'

type Grant = { cap_cents: number; remaining_cents: number; used_cents: number; restored_cents: number } | null
type Problem = { what: string; when: string | null; why: string | null; next_step: string | null }
type Row = {
  partner_id: string
  partner: { id: string; name: string; slug: string; status: string; stitch_funded?: boolean; live_at?: string | null; paused_at?: string | null; removed_at?: string | null; billing_email?: string | null }
  checklist: { item: string; done: boolean; due_now: boolean }[]
  submitted_at: string | null
  email_off_site: string | null
  grant: Grant
  points_waiting: number
  spend?: { problems: Problem[]; sources?: number; last_spend_at?: string | null }
}
const KEY = ['staff', 'partners']
const STATUS_LABEL: Record<string, string> = { draft: 'Draft', pending_review: 'Waiting for review', live: 'Live', paused: 'Paused', removed: 'Removed' }
// every item partners/api.py CHECKLIST_ITEMS can return
export const ITEM_LABEL: Record<string, string> = {
  profile: 'Profile', verify: 'Company details', spend_source: 'Spend source', policy: 'Earn rules', billing: 'Billing',
  terms: 'Terms accepted', team: 'Team', website_button: 'Website button',
}
const label = (item: string) => ITEM_LABEL[item] ?? item.replace(/_/g, ' ')
// "stripe_key connection" reads "Stripe key connection"; a file name stays as it is
const words = (s: string) => { if (s.includes('.')) return s; const w = s.replace(/_/g, ' '); return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() }
const trim = (s: string | null) => (s ?? '').trim().replace(/\.+$/, '')

const COLUMNS = ['Partner', 'Status', 'Since', 'Budget, we pay', 'Used, left', 'Points waiting', 'Last spend', '']
const NUM = new Set(['Budget, we pay', 'Used, left', 'Points waiting'])
const since = (r: Row) => {
  const s = r.partner.status
  return s === 'live' ? r.partner.live_at : s === 'paused' ? r.partner.paused_at : s === 'removed' ? r.partner.removed_at : s === 'pending_review' ? r.submitted_at : null
}

export function PartnersPage() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<{ rows: Row[] }>('GET', '/api/staff/partners'), refetchInterval: 60_000 })
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'draft' ? 'draft' : 'live'
  const [showRemoved, setShowRemoved] = useState(false)
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const all = q.data?.rows ?? []
  const drafts = all.filter((r) => r.partner.status === 'draft')
  // OpenAI and Anthropic are funded by ernd for receipt earning. They are never partners, so they don't count as live.
  const current = all.filter((r) => r.partner.status !== 'draft' && r.partner.status !== 'removed')
  const funded = current.filter((r) => r.partner.stitch_funded)
  const shown = current.filter((r) => !r.partner.stitch_funded)
  const removed = all.filter((r) => r.partner.status === 'removed')
  const rows = tab === 'draft' ? drafts : showRemoved ? [...shown, ...removed] : shown
  const walk = tab === 'draft' ? rows : [...rows, ...funded]
  const isOpen = (r: Row) => toggled[r.partner_id] ?? r.partner.status === 'pending_review'
  const flip = (r: Row) => setToggled((t) => ({ ...t, [r.partner_id]: !isOpen(r) }))
  const { cur } = useCursor(walk, (r) => r.partner_id, flip)
  if (q.isLoading) return <Busy what="Loading partners" />
  if (q.error) return <Page title="Live partners"><Err error={q.error} /></Page>
  return (
    <Page title="Live partners" testid="staff-partners"
      sub={<Sources><Fresh at={q.dataUpdatedAt ? new Date(q.dataUpdatedAt) : null} label="Partners" live limit={180} /></Sources>}
      right={removed.length > 0 && tab === 'live' ? (
        <label className="st-row st-muted">
          <input type="checkbox" checked={showRemoved} onChange={(e) => setShowRemoved(e.target.checked)} data-testid="staff-show-removed" />
          Show removed ({removed.length})
        </label>
      ) : null}>
      <Strip testid="staff-partners-tabs" value={tab} onChange={(t) => setParams(t === 'draft' ? { tab: t } : {}, { replace: true })}
        items={[{ key: 'live', label: 'Live', n: shown.length }, { key: 'draft', label: 'Draft', n: drafts.length }]} />
      {rows.length === 0 ? <p className="st-note">{tab === 'draft' ? 'No drafts.' : 'No live partners.'}</p> : (
        <PartnerTable rows={rows} testid="staff-partner-table" isOpen={isOpen} cur={cur} flip={flip} />
      )}
      {tab === 'live' && funded.length ? (
        <Section title="Funded by ernd" count={funded.length} testid="staff-partners-funded">
          <p className="st-note">Receipt earning. Not counted as live.</p>
          <PartnerTable rows={funded} testid="staff-funded-table" isOpen={isOpen} cur={cur} flip={flip} />
        </Section>
      ) : null}
    </Page>
  )
}

function PartnerTable({ rows, testid, isOpen, cur, flip }: { rows: Row[]; testid: string; isOpen: (r: Row) => boolean; cur: string | null; flip: (r: Row) => void }) {
  return (
    <div className="st-table-wrap">
      <table className="st-table stack" data-testid={testid}>
        <thead><tr>{COLUMNS.map((c) => <th key={c} className={NUM.has(c) ? 'st-num' : undefined}>{c}</th>)}</tr></thead>
        {rows.map((r) => <PartnerRow key={r.partner_id} r={r} open={isOpen(r)} cur={cur === r.partner_id} flip={() => flip(r)} />)}
      </table>
    </div>
  )
}

function PartnerRow({ r, open, cur, flip }: { r: Row; open: boolean; cur: boolean; flip: () => void }) {
  const status = r.partner.status
  const problems = r.spend?.problems.length ?? 0
  const last = r.spend?.last_spend_at
  return (
    <tbody data-testid={`staff-partner-${r.partner.slug}`}>
      <tr data-row={r.partner_id} className={`open${cur ? ' cur' : ''}`} onClick={(e) => { if (!(e.target as HTMLElement).closest('a,button,input,select,textarea,label')) flip() }}>
        <td className="st-primary" data-label="Partner">
          <Link className="st-link" to={`/staff/partner/${r.partner_id}`}>{r.partner.name}</Link>
          {r.email_off_site ? <span className="st-ptn-note st-warn" data-testid="staff-partner-email-off-site">Email not on {r.email_off_site}</span> : null}
        </td>
        <td data-label="Status">
          <span className={status === 'paused' || status === 'removed' ? 'st-warn' : status === 'live' && !r.partner.stitch_funded ? 'st-strong' : 'st-muted'} data-testid="staff-partner-status">{r.partner.stitch_funded ? <span data-testid="staff-partner-house">Funded by ernd, not a signed partner</span> : STATUS_LABEL[status] ?? status}</span>
        </td>
        <td data-label="Since" className="st-nowrap">{since(r) ? when(since(r)!) : '–'}</td>
        <td data-label="Budget, we pay" className="st-num"><span data-testid="staff-grant-cap">{r.grant ? cents(r.grant.cap_cents) : 'not set'}</span></td>
        <td data-label="Used, left" className="st-num"><span data-testid="staff-grant-left">{r.grant ? `${cents(r.grant.used_cents)}, ${cents(r.grant.remaining_cents)}` : '–'}</span></td>
        <td data-label="Points waiting" className="st-num"><span data-testid="staff-points-waiting">{points(r.points_waiting)}</span></td>
        <td data-label="Last spend" className="st-nowrap">
          {!r.spend ? 'Not read' : problems ? <span className="st-bad">{problems} {problems === 1 ? 'problem' : 'problems'}</span> : r.spend.sources === 0 ? 'No source' : last ? when(last) : 'None yet'}
        </td>
        <td data-label=""><Button tone="quiet" aria-expanded={open} onClick={flip} data-testid="staff-partner-open">{open ? 'Close' : 'Open'}</Button></td>
      </tr>
      {open ? <tr><td colSpan={COLUMNS.length} className="st-ptn-detail"><PartnerBlock r={r} /></td></tr> : null}
    </tbody>
  )
}

function PartnerBlock({ r }: { r: Row }) {
  const kit = useKit()
  const live = useStaffAction((id) => `/api/staff/partners/${id}/approve-live`, [KEY])
  const pause = useStaffAction<{ note: string }>((id) => `/api/staff/partners/${id}/pause`, [KEY])
  const resume = useStaffAction((id) => `/api/staff/partners/${id}/resume`, [KEY])
  const remove = useStaffAction<{ note: string }>((id) => `/api/staff/partners/${id}/remove`, [KEY])
  const setGrant = useStaffAction<{ partner_id: string; cap_cents: number; idempotency_key: string }>(() => '/api/staff/grants', [KEY])
  const [why, setWhy] = useState('')
  const [cap, setCap] = useState('')
  const [note, setNote] = useState('')
  const grantKey = useRef<string | null>(null)   // one key per typed amount, so a double press sets the cap once
  const open = r.checklist.filter((c) => c.due_now && !c.done)
  const status = r.partner.status
  const name = r.partner.name
  const capCents = parseDollars(cap)
  const fail = { onError: (e: unknown) => kit.toast(errorText(e), { bad: true, ms: 6_000 }) }

  const doRemove = async () => {
    const ok = await kit.confirm({
      title: `Remove ${name}`, confirm: 'Remove', danger: true,
      body: <>It leaves the site for everyone but staff. Its history stays. This can't be undone here. Why: {why.trim()}</>,
    })
    if (ok.ok) remove.mutate({ id: r.partner_id, body: { note: why } }, fail)
  }
  const doBudget = async () => {
    if (capCents === null) return
    const ok = await kit.confirm({
      title: `Set ${name}'s launch budget to ${cents(capCents)}`, confirm: `Set ${cents(capCents)}`,
      body: <Facts items={[
        ['Now', r.grant ? cents(r.grant.cap_cents) : 'not set'], ['New', cents(capCents)],
        ['Used so far', r.grant ? cents(r.grant.used_cents) : '–'], ['Paid by', 'ernd'],
      ]} />,
    })
    if (!ok.ok) return
    grantKey.current ??= `${r.partner_id}:${capCents}:${Date.now()}`
    setGrant.mutate({ id: '', body: { partner_id: r.partner_id, cap_cents: capCents, idempotency_key: grantKey.current } },
      { onSuccess: () => { setCap(''); grantKey.current = null }, ...fail })
  }
  const doLive = async () => {
    const ok = await kit.confirm({ title: `Put ${name} live`, confirm: 'Approve go-live', body: 'It shows on the site and starts earning for its customers.' })
    if (ok.ok) live.mutate({ id: r.partner_id }, fail)
  }

  const problems = r.spend?.problems ?? []
  return (
    <div className="st-stack">
      {status === 'pending_review' || status === 'draft' ? (
        <p className="st-ptn-check" data-testid="staff-partner-checklist">
          {r.checklist.map((c) => (
            <span key={c.item} className={c.done ? undefined : c.due_now ? 'st-bad' : 'st-muted'}>
              {label(c.item)} {c.done ? 'done' : c.due_now ? 'missing' : 'later'}
            </span>
          ))}
        </p>
      ) : (
        // once live, the set-up list is history: one quiet line
        <p className="st-muted" data-testid="staff-partner-checklist">
          Set-up {r.checklist.filter((c) => c.done).length} of {r.checklist.length} done
          {r.checklist.some((c) => !c.done) ? `, open: ${r.checklist.filter((c) => !c.done).map((c) => label(c.item).toLowerCase()).join(', ')}` : ''}
        </p>
      )}
      <SpendLine spend={r.spend} />
      {problems.length ? (
        <ul className="st-ptn-problems" data-testid="staff-spend-problems">
          {problems.map((p, i) => (
            <li key={i} data-testid="staff-spend-problem">
              <span className="st-strong">{words(p.what)}</span>{p.when ? <span className="st-muted">, {when(p.when)}</span> : null}: {trim(p.why)}
              {p.next_step ? <span className="st-muted">. {trim(p.next_step)}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="st-row st-ptn-do">
        {status === 'pending_review' ? (
          <Button tone="primary" data-testid="staff-approve-live" disabled={open.length > 0 || live.isPending}
            title={open.length ? `Open: ${open.map((c) => label(c.item)).join(', ')}` : undefined} onClick={() => void doLive()}>Approve go-live</Button>
        ) : null}
        {status === 'pending_review' && open.length ? <span className="st-muted">Open: {open.map((c) => label(c.item)).join(', ')}</span> : null}
        {status === 'live' ? (
          <>
            <input className="st-input" placeholder="Why pause" value={note} onChange={(e) => setNote(e.target.value)} data-testid="staff-pause-note" />
            <Button data-testid="staff-pause" disabled={!note.trim() || pause.isPending} title={note.trim() ? undefined : 'Write why you pause first'}
              onClick={() => pause.mutate({ id: r.partner_id, body: { note } }, { onSuccess: () => setNote(''), ...fail })}>Pause</Button>
          </>
        ) : null}
        {status === 'paused' ? (
          <>
            <Button tone="primary" data-testid="staff-resume" disabled={resume.isPending} onClick={() => resume.mutate({ id: r.partner_id }, fail)}>Resume</Button>
            <input className="st-input" placeholder="Why remove" value={why} onChange={(e) => setWhy(e.target.value)} data-testid="staff-remove-note" />
            <Button tone="danger" data-testid="staff-remove" disabled={!why.trim() || remove.isPending} title={why.trim() ? undefined : 'Write why you remove it first'}
              onClick={() => void doRemove()}>Remove</Button>
          </>
        ) : null}
        <span className="st-ptn-gap" />
        <input className="st-input st-ptn-money" placeholder="New budget in $" value={cap} onChange={(e) => { setCap(e.target.value); grantKey.current = null }}
          data-testid="staff-grant-input" onKeyDown={(e) => { if (e.key === 'Enter') void doBudget() }} />
        <Button data-testid="staff-grant-set" disabled={capCents === null || setGrant.isPending}
          title={capCents === null ? 'Type the budget in dollars, like 500 or 500.00' : undefined} onClick={() => void doBudget()}>Set budget</Button>
      </div>
    </div>
  )
}

/** What the spend check really covers: connection and file errors. It can't say spend is arriving, so it says when it last did. */
function SpendLine({ spend }: { spend: Row['spend'] }) {
  if (!spend) return <p className="st-muted">Spend not read</p>
  if (spend.problems.length) return <p className="st-bad st-strong">Spend problems: {spend.problems.length}</p>
  const last = spend.last_spend_at
  return (
    <p className="st-muted" data-testid="staff-spend-ok">
      No spend errors.{' '}
      {spend.sources === 0 ? 'No spend source connected' : last ? `Spend last arrived ${when(last)}` : 'No spend has arrived yet'}
    </p>
  )
}
